import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ButtonPropsWithLabel } from 'src/components/Dialog';
import {
  fetchInvoices,
  isPayableOpenInvoice,
  pastDueInvoicesQueryKey,
} from 'src/models/payment/payment';
import { formatCurrency } from 'src/utils/numberFormatter';

import { BlockingReasonFallback, BlockingReasonRow } from './BlockingReasonRow';
import { BlockingReasonVariant } from './_types';
import { blockingReasonTranslationKey } from './blockingReasonTranslationKey';

export function PaymentRequiredReason({
  actions,
  userId,
  resolved = false,
  variant = 'checkin',
}: {
  actions?: ButtonPropsWithLabel[];
  userId: string;
  resolved?: boolean;
  variant?: BlockingReasonVariant;
}) {
  const { t } = useTranslation('translation', { keyPrefix: 'checkinBlocker' });

  const { data: invoicesPayload, isLoading: isLoadingInvoices } = useQuery({
    queryKey: pastDueInvoicesQueryKey(userId),
    queryFn: () =>
      fetchInvoices({
        userId,
        per: 100,
        filters: { pastDue: true },
      }),
  });

  const invoices = invoicesPayload?.invoices ?? [];

  const payableInvoices = invoices.filter(isPayableOpenInvoice);

  const totalAmountDue = payableInvoices.reduce(
    (acc, invoice) => acc + (invoice.amountDue ?? 0),
    0
  );

  if (isLoadingInvoices && !resolved) {
    return <BlockingReasonFallback />;
  }

  return (
    <BlockingReasonRow
      title={
        totalAmountDue > 0
          ? t(
              blockingReasonTranslationKey(variant, 'paymentRequired', 'title'),
              {
                amount: formatCurrency(totalAmountDue),
              }
            )
          : t(
              blockingReasonTranslationKey(
                variant,
                'paymentRequired',
                'titleNoAmount'
              )
            )
      }
      message={t(
        blockingReasonTranslationKey(variant, 'paymentRequired', 'message')
      )}
      actions={resolved ? undefined : actions}
      color={resolved ? 'success' : 'error'}
    />
  );
}
